// Global data's Drive sync: one connection to a shared drive, the global folders added
// from it and its individually tracked files. See docs/Y2026-Q3/design-global-drive-sync.md.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import type { QueryClient } from "react-query";

import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";
import type { DriveBrowseResponse, DriveBrowseSource } from "./useDriveSync";
import { globalDriveFileState, isRequirementsExtracting } from "./useGlobalData";
import type { GlobalDataRecord } from "./useGlobalData";

// Mirrors GLOBAL_DRIVE_CONNECTION_ID on the BE: sync-now and files take it for the tracked files.
export const GLOBAL_DRIVE_CONNECTION_ID = "global-drive-connection";

// A tracked file an agent wrote for a client: a client knowledge base.
export interface GlobalKnowledgeBase {
  document_id: string;
  kept_by: string;
  write_count: number;
  last_written_by: string | null;
  last_written_at: string;
}

export interface GlobalFileLoad {
  projects: { project_id: string; name: string }[];
  // What one turn of an agent it is loaded for carries; null until it is synced.
  loaded_chars: number | null;
  loaded_tokens: number | null;
  // Agents whose loaded files together pass the per-turn limit: some are listed instead.
  over_limit_for: string[];
}

export interface GlobalTrackedFile {
  file_id: string;
  name: string;
  drive_path: string;
  added_by: string;
  added_at: string | null;
  tenant_scope: string | null;
  landlord_scope: string | null;
  // The agents whose context carries its text, set by the agent that wrote it; null for
  // every agent to read or acknowledge.
  load_for: string[] | null;
  knowledge_base: GlobalKnowledgeBase | null;
  load: GlobalFileLoad;
  synced: GlobalDataRecord | null;
}

export interface KnowledgeBaseGap {
  kind: "tenant" | "landlord";
  client: string;
  project_count: number;
}

export interface GlobalDriveConnection {
  connected: boolean;
  authorized_user_email: string;
  drive_id: string | null;
  drive_name: string;
  last_synced_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  files: GlobalTrackedFile[];
  knowledge_base_gaps: KnowledgeBaseGap[];
  active_sync_operation_key: string | null;
}

interface ConnectionResponse {
  connection: GlobalDriveConnection;
}

export interface UpdateGlobalDriveFolderInput {
  global_data_id: string;
  title: string;
  tenant_scope: string | null;
  landlord_scope: string | null;
  exclude_name_patterns: string[];
  excluded_subfolder_ids: string[];
  // Files ticked off and back on; every other file keeps its exclusion.
  exclude_file_ids: string[];
  include_file_ids: string[];
  // Names of what was ticked or unticked, for the Settings history.
  names: Record<string, string>;
  enabled: boolean;
}

export interface UpdateGlobalTrackedFileInput {
  file_id: string;
  tenant_scope: string | null;
  landlord_scope: string | null;
}

export interface GlobalDriveFolderSyncStart {
  operation_key: string;
  status: string;
}

const POLL_INTERVAL_MS = 4000;

const applyConnection = (qc: QueryClient, resp: ConnectionResponse) => {
  qc.setQueryData([QK.globalDriveConnection], resp.connection);
  void qc.invalidateQueries([QK.globalDriveConnection]);
  void qc.invalidateQueries([QK.globalDataList]);
  void qc.invalidateQueries([QK.globalDriveBrowse]);
  void qc.invalidateQueries([QK.globalDriveFiles]);
};

// Writing the key onto the cached list lets a mounted folder row adopt the new
// sync's progress poll without waiting for a refetch.
const adoptSyncOperation = (
  qc: QueryClient,
  globalDataId: string,
  operationKey: string,
) => {
  qc.setQueryData<GlobalDataRecord[] | undefined>([QK.globalDataList], (records) =>
    records?.map((r) =>
      r.global_data_id === globalDataId
        ? { ...r, active_sync_operation_key: operationKey }
        : r,
    ),
  );
};

// ``poll`` keeps the state refreshing while a tracked-files sync is running.
export const useGlobalDriveConnection = (options?: { poll?: boolean }) => {
  const http = useAgentsHttp();
  return useQuery<GlobalDriveConnection>(
    [QK.globalDriveConnection],
    async () => (await http<ConnectionResponse>("/global-data/drive/state", {})).connection,
    {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      refetchInterval: (data) =>
        options?.poll ||
        (data?.files ?? []).some(
          (f) => f.synced !== null && isRequirementsExtracting(f.synced.requirements_status),
        )
          ? POLL_INTERVAL_MS
          : false,
    },
  );
};

export const useConnectGlobalDrive = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ConnectionResponse, Error, { drive_id: string }>(
    (body) => http<ConnectionResponse>("/global-data/drive/connect", body),
    { meta: { silentError: true }, onSuccess: (resp) => applyConnection(qc, resp) },
  );
};

export const useDisconnectGlobalDrive = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ConnectionResponse, Error, void>(
    () => http<ConnectionResponse>("/global-data/drive/disconnect", {}),
    { onSuccess: (resp) => applyConnection(qc, resp) },
  );
};

export const useGlobalDriveBrowseSource = (): DriveBrowseSource => {
  const http = useAgentsHttp();
  return useMemo(
    () => ({
      queryKey: [QK.globalDriveBrowse],
      browse: (where) =>
        http<DriveBrowseResponse>("/global-data/drive/browse", {
          folder_id: where.folderId ?? undefined,
          query: where.query,
          search_type: where.searchType,
        }),
    }),
    [http],
  );
};

export const useAddGlobalDriveSources = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    ConnectionResponse & { operation_keys: string[]; awaiting_review_ids: string[] },
    Error,
    { folder_ids: string[]; file_ids: string[]; actor_name: string }
  >((body) => http("/global-data/drive/add-sources", body), {
    onSuccess: (resp) => applyConnection(qc, resp),
  });
};

export const useUpdateGlobalDriveFolder = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ global_data: GlobalDataRecord }, Error, UpdateGlobalDriveFolderInput>(
    (input) =>
      http<{ global_data: GlobalDataRecord }>("/global-data/drive-folder/update", input),
    {
      meta: { silentError: true },
      onSuccess: () => {
        void qc.invalidateQueries([QK.globalDataList]);
        void qc.invalidateQueries([QK.globalDriveBrowse]);
      },
    },
  );
};

export const useUpdateGlobalTrackedFile = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ConnectionResponse, Error, UpdateGlobalTrackedFileInput>(
    (input) => http<ConnectionResponse>("/global-data/drive/file/update", input),
    { meta: { silentError: true }, onSuccess: (resp) => applyConnection(qc, resp) },
  );
};

export const useRemoveGlobalTrackedFile = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ConnectionResponse, Error, { file_id: string }>(
    (body) => http<ConnectionResponse>("/global-data/drive/file/remove", body),
    { onSuccess: (resp) => applyConnection(qc, resp) },
  );
};

// Queue a synced file whose extraction failed; the files list polls while it extracts.
export const useRetryGlobalDriveFile = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ global_data_id: string }, Error, { global_data_id: string }>(
    (body) => http<{ global_data_id: string }>("/global-data/drive/file/retry", body),
    {
      onSuccess: () => {
        void qc.invalidateQueries([QK.globalDriveFiles]);
        void qc.invalidateQueries([QK.globalDriveConnection]);
        void qc.invalidateQueries([QK.globalDataList]);
      },
    },
  );
};

// Pass a folder's id, or GLOBAL_DRIVE_CONNECTION_ID for the tracked files. Returns the
// in-flight sync's operation_key when one is already running.
export const useSyncGlobalDriveFolder = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<GlobalDriveFolderSyncStart, Error, { global_data_id: string }>(
    (body) => http<GlobalDriveFolderSyncStart>("/global-data/drive-folder/sync-now", body),
    {
      meta: { silentError: true },
      onSuccess: (resp, vars) => {
        adoptSyncOperation(qc, vars.global_data_id, resp.operation_key);
      },
    },
  );
};

// Every synced Drive file in global data, removed ones included, ordered by drive_path.
export const useGlobalDriveFiles = (options: { poll: boolean }) => {
  const http = useAgentsHttp();
  return useQuery<GlobalDataRecord[]>(
    [QK.globalDriveFiles],
    async () =>
      (await http<{ files: GlobalDataRecord[] }>("/global-data/drive/files", {})).files,
    {
      refetchOnWindowFocus: false,
      refetchInterval: (data) =>
        options.poll ||
        (data ?? []).some((f) => globalDriveFileState(f) === "extracting")
          ? POLL_INTERVAL_MS
          : false,
    },
  );
};
